import { useParams } from 'react-router-dom';
import { Row, Col, Alert, Card, CardBody } from 'reactstrap';
import { useBrand } from '../../brand/BrandProvider';
import { useBrandStats } from './useBrandStats';
import { KpiCard } from '../../components/KpiCard';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { QuickLinks } from './QuickLinks';

const fmtNum = new Intl.NumberFormat('de-DE');

function makeEurFmt(currency = 'EUR') {
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    });
  } catch {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  }
}

/**
 * Brand-Dashboard — KPI-Karten + Quick-Links.
 *
 * Source: `GET /v1/admin/stats` (Live-Shape per 2026-06 — flach, ohne
 * YTD/Trend/Uptime; Strings für Zahlen). Normalisierung in `useBrandStats`.
 */
export function BrandDashboard() {
  const { brandSlug } = useParams();
  const brand = useBrand();
  const stats = useBrandStats(brand.currentBrandOrigin);

  if (stats.isLoading) {
    return (
      <div>
        <h1 className="h3 mb-4">Brand Dashboard</h1>
        <QueryLoadingState label="Brand-Statistiken werden geladen…" />
      </div>
    );
  }

  if (stats.isError) {
    return (
      <div>
        <h1 className="h3 mb-4">Brand Dashboard</h1>
        <QueryErrorState error={stats.error} onRetry={() => stats.refetch()} />
      </div>
    );
  }

  const data = stats.data;
  if (!data) {
    return (
      <div>
        <h1 className="h3 mb-4">Brand Dashboard</h1>
        <Alert color="warning">Keine Statistik-Daten vom Server.</Alert>
      </div>
    );
  }

  const fmtEur = makeEurFmt(data.revenue.currency);
  const conversionPct = data.revenue.totalOrders > 0
    ? (data.revenue.paidCount / data.revenue.totalOrders) * 100
    : null;
  const avgOrderEur = data.revenue.paidCount > 0
    ? data.revenue.paidTotal / data.revenue.paidCount
    : null;

  return (
    <div>
      <div className="d-flex align-items-baseline justify-content-between mb-4">
        <div>
          <h1 className="h3 mb-1">{brand.currentBrand?.displayName ?? 'Brand'} · Dashboard</h1>
          <p className="text-muted small mb-0">
            Stand: {new Date(data.generatedAt).toLocaleString('de-DE')} · GET /v1/admin/stats
          </p>
        </div>
      </div>

      {/* Revenue-Reihe */}
      <h6 className="text-uppercase small text-muted mb-2">Revenue</h6>
      <Row className="g-3 mb-4">
        <Col md="6" lg="3">
          <KpiCard
            label="Bezahlt (kumuliert)"
            value={fmtEur.format(data.revenue.paidTotal)}
            sublabel={`${data.revenue.paidCount} bezahlte Orders`}
            accent="success"
          />
        </Col>
        <Col md="6" lg="3">
          <KpiCard
            label="∅ Order-Wert"
            value={avgOrderEur != null ? fmtEur.format(avgOrderEur) : '—'}
            sublabel={data.revenue.paidCount > 0 ? `aus ${data.revenue.paidCount} Orders` : 'keine bezahlten Orders'}
            accent="primary"
          />
        </Col>
        <Col md="6" lg="3">
          <KpiCard
            label="Total Orders"
            value={fmtNum.format(data.revenue.totalOrders)}
            sublabel={`davon ${data.revenue.paidCount} bezahlt`}
            accent="info"
          />
        </Col>
        <Col md="6" lg="3">
          <KpiCard
            label="Conversion"
            value={conversionPct != null ? `${conversionPct.toFixed(1).replace('.', ',')} %` : '—'}
            sublabel="bezahlt / total"
            accent={conversionPct != null && conversionPct >= 60 ? 'success' : 'warning'}
          />
        </Col>
      </Row>

      {/* Users-Reihe */}
      <h6 className="text-uppercase small text-muted mb-2">Users</h6>
      <Row className="g-3 mb-4">
        <Col md="4">
          <KpiCard
            label="Total User"
            value={fmtNum.format(data.users.total)}
            accent="info"
          />
        </Col>
        <Col md="4">
          <KpiCard
            label="Active"
            value={fmtNum.format(data.users.active)}
            sublabel={`${((data.users.active / Math.max(1, data.users.total)) * 100).toFixed(0)} % aktiv`}
            accent="success"
          />
        </Col>
        <Col md="4">
          <KpiCard
            label="Suspended"
            value={fmtNum.format(data.users.suspended)}
            accent={data.users.suspended > 0 ? 'warning' : 'secondary'}
          />
        </Col>
      </Row>

      {/* Services-Reihe */}
      <h6 className="text-uppercase small text-muted mb-2">Services</h6>
      <Row className="g-3 mb-4">
        <Col md="3">
          <KpiCard
            label="Total Services"
            value={fmtNum.format(data.services.total)}
            accent="info"
          />
        </Col>
        <Col md="3">
          <KpiCard
            label="Active"
            value={fmtNum.format(data.services.active)}
            accent="success"
          />
        </Col>
        <Col md="3">
          <KpiCard
            label="Suspended"
            value={fmtNum.format(data.services.suspended)}
            accent={data.services.suspended > 0 ? 'warning' : 'secondary'}
          />
        </Col>
        <Col md="3">
          <KpiCard
            label="Failed"
            value={fmtNum.format(data.services.failed)}
            accent={data.services.failed > 0 ? 'danger' : 'secondary'}
          />
        </Col>
      </Row>

      {/* Quick-Links + Hinweise */}
      <Row className="g-3">
        <Col lg="8">
          <Card>
            <CardBody>
              <h5 className="mb-2">Was hier (noch) nicht ist</h5>
              <p className="text-muted small mb-2">
                Der aktuelle <code>/v1/admin/stats</code>-Endpoint liefert eine
                Snapshot-Sicht ohne Zeit-Partitionierung. Wenn das Core-Backend
                folgende Felder ergänzt, schalten wir hier Kacheln frei:
              </p>
              <ul className="small text-muted mb-0">
                <li><code>revenue.currentMonth</code> / <code>ytd</code> mit gross+net → MRR-Karte</li>
                <li><code>revenue.trend12Months[]</code> → Umsatz-Chart der letzten 12 Monate</li>
                <li><code>uptime.last30Days</code> / <code>last90Days</code> → Uptime-Kachel</li>
                <li><code>services.pending</code> → Provisioning-Pipeline-Indikator</li>
                <li><code>users.deleted</code> → Anonymisierungs-Counter (DSGVO Art. 17)</li>
              </ul>
            </CardBody>
          </Card>
        </Col>
        <Col lg="4">
          <QuickLinks brandSlug={brandSlug} />
        </Col>
      </Row>
    </div>
  );
}
